import axios from 'axios';
import { toast } from 'sonner';
import {
  Edit2, Trash2, X, Square, CheckSquare,
} from 'lucide-react';
import { UserAssignDropdown } from './UserAssignDropdown';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const getStatusColor = (status) => {
  switch (status) {
    case 'ENTREGUE': return 'bg-green-500 text-white';
    case 'ATRASADO': return 'bg-red-500 text-white';
    default: return 'bg-orange-500 text-white';
  }
};

const getStatusBorderColor = (status) => {
  switch (status) {
    case 'ENTREGUE': return 'border-green-500/50';
    case 'ATRASADO': return 'border-red-500/50';
    default: return 'border-orange-500/50';
  }
};

/**
 * Single character card (extracted from ProjectDetails.js).
 * Keeps identical testids + behavior.
 */
const CharacterCard = ({
  character, isAdmin, user, allUsers, projectId,
  selected, onToggleSelect, onRefresh, onEdit, onDelete,
  onStatusChange, canChangeStatus, getAuthHeaders,
}) => {
  const assignedUsers = character.assigned_users || [];
  const hasMultiple = assignedUsers.length > 1;

  const handleRename = async (newName) => {
    if (!newName || newName === character.name) return;
    try {
      const fd = new FormData();
      fd.append('name', newName);
      await axios.put(`${API}/projects/${projectId}/characters/${character.id}`, fd, { headers: getAuthHeaders() });
      onRefresh();
    } catch { toast.error('Erro ao renomear'); }
  };

  const handleUpdateFalas = async (val) => {
    try {
      const fd = new FormData();
      fd.append('num_falas', val);
      await axios.put(`${API}/projects/${projectId}/characters/${character.id}`, fd, { headers: getAuthHeaders() });
    } catch (err) { console.error('Failed to update falas:', err); }
  };

  const handleRemoveUser = async (userId) => {
    try {
      const fd = new FormData();
      fd.append('user_id', userId);
      await axios.post(`${API}/projects/${projectId}/characters/${character.id}/remove-user`, fd, { headers: getAuthHeaders() });
      onRefresh();
    } catch { toast.error('Erro'); }
  };

  const handleAddUser = async (userId) => {
    if (!userId) return;
    try {
      const fd = new FormData();
      fd.append('user_id', userId);
      await axios.post(`${API}/projects/${projectId}/characters/${character.id}/add-user`, fd, { headers: getAuthHeaders() });
      toast.success('Dublador adicionado!');
      onRefresh();
    } catch { toast.error('Erro ao adicionar'); }
  };

  return (
    <div
      className={`bg-slate-700/50 rounded-lg p-4 border-2 transition-all ${selected ? 'border-teal-500 ring-1 ring-teal-500/30' : getStatusBorderColor(character.status)}`}
      data-testid={`character-card-${character.id}`}
    >
      <div className="flex justify-between items-start mb-2">
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button onClick={onToggleSelect} data-testid={`select-char-${character.id}`}>
              {selected ? <CheckSquare className="h-4 w-4 text-teal-400" /> : <Square className="h-4 w-4 text-slate-500 hover:text-slate-300" />}
            </button>
          )}
          {isAdmin ? (
            <input
              type="text"
              defaultValue={character.name}
              onBlur={(e) => handleRename(e.target.value.trim())}
              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
              className="text-white font-bold text-lg bg-transparent border-b border-transparent hover:border-slate-600 focus:border-teal-500 focus:outline-none px-0 py-0 w-full"
              data-testid={`edit-name-${character.id}`}
            />
          ) : (
            <h3 className="text-white font-bold text-lg">{character.name}</h3>
          )}
        </div>
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(character.status)}`}>
          {character.status}
        </span>
      </div>

      {/* Falas */}
      <div className="flex items-center gap-3 mb-2">
        {isAdmin ? (
          <div className="flex items-center gap-1">
            <input type="number" min="0" defaultValue={character.num_falas || 0}
              onBlur={(e) => handleUpdateFalas(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
              className="bg-slate-700 border border-slate-600 rounded px-2 py-0.5 text-sm text-slate-300 font-mono w-16 focus:outline-none focus:border-teal-500"
              data-testid={`falas-${character.id}`}
            />
            <span className="text-xs text-slate-400">falas</span>
          </div>
        ) : (
          character.num_falas > 0 && <span className="text-xs text-slate-400 bg-slate-700 px-2 py-0.5 rounded">{character.num_falas} falas</span>
        )}
      </div>

      {/* Inline User Assignment (Admin only) */}
      {isAdmin ? (
        <div className="mb-3">
          <div className="flex flex-wrap gap-1 mb-1.5">
            {assignedUsers.map(u => (
              <span key={u.id} className="inline-flex items-center gap-1 bg-teal-500/20 text-teal-400 px-2 py-0.5 rounded text-xs">
                {u.name}
                <button onClick={(e) => { e.stopPropagation(); handleRemoveUser(u.id); }}
                  className="text-teal-300 hover:text-red-400 ml-0.5" data-testid={`remove-user-${character.id}-${u.id}`}>
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
          {(!assignedUsers.length) && !character.assigned_user_name && (
            <div className="bg-red-500/15 border border-red-500/30 rounded-lg px-3 py-1.5 mb-1.5" data-testid={`unassigned-${character.id}`}>
              <span className="text-red-400 text-xs font-bold">NÃO ATRIBUÍDO</span>
            </div>
          )}
          <UserAssignDropdown
            users={allUsers}
            currentUserId={''}
            onAssign={handleAddUser}
            testId={`inline-assign-${character.id}`}
          />
        </div>
      ) : (
        (assignedUsers.length > 0 || character.assigned_user_name) ? (
          <p className="text-teal-400 text-sm mb-3">
            Dublador(es): {assignedUsers.length > 0
              ? assignedUsers.map(u => u.name).join(', ')
              : character.assigned_user_name}
          </p>
        ) : (
          <div className="bg-red-500/15 border border-red-500/30 rounded-lg px-3 py-1.5 mb-3">
            <span className="text-red-400 text-xs font-bold">NÃO ATRIBUÍDO</span>
          </div>
        )
      )}

      {/* Actions */}
      <div className="flex gap-2 flex-wrap items-center">
        {canChangeStatus(character) && (() => {
          // Per-user toggles when > 1 dublador
          if (hasMultiple) {
            const perUser = character.per_user_status || {};
            return (
              <div className="flex flex-col gap-1.5 w-full" data-testid={`per-user-status-${character.id}`}>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Entrega por dublador</p>
                {assignedUsers.map(au => {
                  const delivered = perUser[au.id] === 'ENTREGUE';
                  const canFlip = isAdmin || au.id === user?.id;
                  return (
                    <div key={au.id} className="flex items-center justify-between gap-2 bg-slate-800/40 border border-slate-700 rounded-md px-2 py-1">
                      <span className="text-sm text-white truncate flex-1" title={au.name}>{au.name}</span>
                      <button
                        type="button"
                        disabled={!canFlip}
                        onClick={() => onStatusChange(character.id, delivered ? 'PENDENTE' : 'ENTREGUE', au.id)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 ${
                          delivered ? 'bg-green-500' : 'bg-slate-600'
                        } ${!canFlip ? 'opacity-40 cursor-not-allowed' : ''}`}
                        data-testid={`per-user-toggle-${character.id}-${au.id}`}
                        title={canFlip ? (delivered ? 'Marcar como Pendente' : 'Marcar como Entregue') : 'Apenas o próprio dublador ou admin pode alterar'}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                          delivered ? 'translate-x-5' : 'translate-x-0.5'
                        }`} />
                      </button>
                    </div>
                  );
                })}
              </div>
            );
          }
          // Legacy single-user toggle
          return (
            <button
              onClick={() => onStatusChange(character.id, character.status === 'ENTREGUE' ? 'PENDENTE' : 'ENTREGUE')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                character.status === 'ENTREGUE' ? 'bg-green-500' : 'bg-slate-600'
              }`}
              data-testid={`toggle-character-${character.id}`}
              title={character.status === 'ENTREGUE' ? 'Marcar como Pendente' : 'Marcar como Entregue'}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                character.status === 'ENTREGUE' ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          );
        })()}

        {isAdmin && (
          <>
            <button
              onClick={() => onEdit(character)}
              className="flex items-center gap-1 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 px-2 py-1 rounded text-xs transition-colors"
              data-testid={`edit-character-${character.id}`}
            >
              <Edit2 className="h-3 w-3" />
              Editar
            </button>
            <button
              onClick={() => onDelete(character.id)}
              className="flex items-center gap-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 px-2 py-1 rounded text-xs transition-colors"
              data-testid={`delete-character-${character.id}`}
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default CharacterCard;
